//
//  institutions.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module defines the guides of the trial institution and institution pages.
//

import { Permission } from "src/api/Tmd.dtos";

import {
    emailRecipientsGuide,
    entryGuide,
    notesGuides,
    participantColumns,
    participantGuide,
    versionStops
} from "./commonGuides";
import {
    actionsStop,
    column,
    covering,
    createGuide,
    field,
    filterStop,
    grid,
    item,
    menu,
    saveStop,
    type PageGuide,
    type PageGuideStop
} from "./pageGuide";
import {
    roleCreateGuide,
    roleEditGuide,
    roleListGuide,
    type RoleOwner
} from "./personnelRoleGuides";

/**
 * An address block: the tour visits its first line and the help lists the others.
 *
 * Preconditions: `names` gives the block's other property paths in page order: line 2, suburb,
 * postcode, state, state other and country.
 */
const address = (first: string, label: string, text: string, names: string[]): PageGuideStop =>
    covering(
        field(first, label, text),
        ...names.map(
            (name, index) =>
                [
                    name,
                    [
                        "Address Line 2",
                        "Suburb/City",
                        "ZIP/Postal Code",
                        "State/Province/Region",
                        "State Other, enabled when the state is Other",
                        "Country"
                    ][index]
                ] as [string, string]
        )
    );

const trialInstitution: PageGuide = {
    intro: "One institution's participation in the trial: its status, governance and activation dates, accrual, and the capabilities and tags that describe the site. Everything else about the site is on the side menu.",
    stops: [
        field(
            "institutionId",
            "Institution",
            "The hospital or treatment centre taking part. Institutions are maintained under Institutions; each can take part in a trial once."
        ),
        field(
            "groupId",
            "Group",
            "The cooperative group the site takes part through. It is used for group-level items and reporting."
        ),
        field(
            "trialInstitutionStatusId",
            "Status",
            "Where the site is, from start-up through open to recruitment to closed. The status is shown as a coloured badge in the trial's institution list."
        ),
        field(
            "capabilities",
            "Capabilities",
            "The trial's capabilities this site has, such as a treatment technique. The Capabilities section shows whether the site, with its supporting sites, covers every capability the trial requires. Shown only when the trial uses capabilities."
        ),
        field(
            "tags",
            "Tags",
            "The trial's institution tags that describe this site. Tags and capabilities can decide which institution items apply to the site. Shown only when the trial uses tags."
        ),
        field(
            "rgoSubmissionDate",
            "RGO Submission Date",
            "When the site-specific governance application was submitted to the research governance office."
        ),
        field(
            "rgoApprovalDate",
            "RGO Approval Date",
            "When research governance approval was granted, which usually allows the site to be activated."
        ),
        field(
            "initationMeetingDate",
            "Date of Initiation Meeting",
            "When the site initiation meeting, which trains site staff on the protocol, was held."
        ),
        field(
            "dateActivated",
            "Date Activated",
            "When the site opened to recruitment. It is shown in the trial's institution list and used in activation reports."
        ),
        field(
            "firstPatient",
            "Date of first participant on study",
            "When the site registered its first participant, for reporting the time from activation to first recruitment."
        ),
        field(
            "closureDate",
            "Date of closure of site",
            "When the site closed to the trial. Record it along with a closed status when the site stops taking part."
        ),
        field(
            "expectedAccrual",
            "Expected Accrual",
            "How many participants the site expects to recruit. The trial's institution list shows actual accrual against this target."
        ),
        field(
            "totalAccrual",
            "Actual Accrual",
            "How many participants the site has recruited, as reported for the trial."
        ),
        field(
            "comments",
            "Comments",
            "Anything worth knowing about the site's participation, such as delays in approval or staffing issues."
        ),
        field(
            "active",
            "Active",
            "Clear to hide the site from the trial's lists. Its history, personnel and participants are kept."
        ),
        saveStop(
            "Submit saves the site. Changes to its tags or capabilities update which institution items apply to it.",
            Permission.TrialInstitutionUpdate
        )
    ],
    rules: ["Institution and Group are required."]
};

const trialInstitutionDetails: PageGuide = {
    ...trialInstitution,
    stops: [
        ...trialInstitution.stops,
        menu(
            item("Details", "This page: the site's status, dates, accrual, capabilities and tags."),
            item(
                "Supporting Sites",
                "Other institutions that deliver part of the trial for this site, such as a satellite treatment centre."
            ),
            item(
                "Capabilities",
                "Whether the site, with its supporting sites, covers each capability the trial requires. Shown when the trial uses capabilities."
            ),
            item(
                "Participants",
                "The participants registered at this site, and where new participants are registered."
            ),
            item(
                "Items",
                "The items the site completes for the trial, such as agreements and governance approvals, with their versions and data."
            ),
            item(
                "Personnel",
                "The people with roles at the site for this trial, such as the principal investigator and data manager."
            ),
            item("Notes", "Notes recorded about the site's participation in the trial."),
            item(
                "Emails",
                "Who receives the emails sent about this site, overriding the trial's recipients if needed."
            )
        )
    ]
};

const supportingSite: PageGuide = {
    intro: "A supporting site: another institution that delivers part of the trial for this site, such as a satellite treatment centre. Its capabilities count towards this site's capabilities.",
    stops: [
        field(
            "institutionId",
            "Institution",
            "The institution providing support. It is chosen from the institutions maintained under Institutions."
        ),
        field(
            "capabilities",
            "Capabilities",
            "The capabilities the supporting site provides for this site. They count when checking whether this site covers the trial's required capabilities."
        ),
        field(
            "tags",
            "Tags",
            "The trial's institution tags that describe the supporting site, in the same way as for a site."
        ),
        field(
            "comments",
            "Comments",
            "What the supporting site does for this site, such as delivering radiotherapy for participants recruited here."
        ),
        field(
            "rank",
            "Rank",
            "The order supporting sites are listed in; lower numbers are listed first."
        ),
        field(
            "active",
            "Active",
            "Clear to archive the supporting site when it stops supporting this site. It stays in the list as an archived record."
        ),
        saveStop(
            "Submit saves the supporting site and updates this site's capability coverage.",
            Permission.TrialInstitutionUpdate
        )
    ]
};

const siteItem: PageGuide = {
    intro: "One item this site completes for the trial, such as an agreement or a governance approval: its template, its current version and its version history. Enter Data opens the collected data.",
    stops: [
        field(
            "trialInstitutionItemTemplateId",
            "Trial Institution Item Template",
            "The template the item was created from, which sets the questions it collects. It cannot be changed once the item exists."
        ),
        ...versionStops(),
        field(
            "currentVersion.trialItemStatusId",
            "Status",
            "The current version's review state. Submit its data before marking it Complete."
        ),
        field(
            "active",
            "Active",
            "Clear to hide the item from the site's items, for example when it no longer applies."
        ),
        saveStop(
            "Submit saves the item's details. The collected data is saved on the Enter Data page.",
            Permission.TrialInstitutionItemInstanceUpdate
        )
    ]
};

const institution: PageGuide = {
    intro: "An institution's details: its identity, addresses and contacts, research governance office, and the legal details used on agreements. Its departments, personnel, QA forms and notes are on the side menu.",
    stops: [
        field(
            "name",
            "Name",
            "The institution's usual name, shown throughout the system and in emails."
        ),
        field(
            "formerNames",
            "Former Names",
            "Earlier names, so the institution can still be found when searching for them."
        ),
        field(
            "institutionTypeId",
            "Institution Type",
            "Classifies the institution, for example public or private hospital. Types are maintained in settings."
        ),
        field(
            "code",
            "Code",
            "The short code identifying the institution in lists and reports. It is set when the institution is created and cannot be changed afterwards."
        ),
        field(
            "siteLocationId",
            "Site location/area",
            "Where the institution is, such as metropolitan or regional, for reporting."
        ),
        field(
            "facilityAllianceMemberId",
            "Facility Alliance Member",
            "The facility alliance organisation the institution belongs to, if any."
        ),
        address(
            "address",
            "Street address",
            "The institution's street address. Choose the country first so the right states are offered.",
            ["address2", "suburb", "postcode", "stateId", "stateOther", "countryId"]
        ),
        address(
            "mailAddress",
            "Postal address",
            "Where post should be sent, if different from the street address.",
            [
                "mailAddress2",
                "mailSuburb",
                "mailPostcode",
                "mailStateId",
                "mailStateOther",
                "mailCountryId"
            ]
        ),
        covering(
            field(
                "phone",
                "Phone",
                "The institution's main switchboard number, used when contacting the institution rather than a person."
            ),
            ["fax", "Fax"]
        ),
        field(
            "website",
            "Website",
            "The institution's website address, including https://, so it opens from the institution record."
        ),
        covering(
            field(
                "researchGovernanceOfficeName",
                "Research Governance Office (RGO) Name",
                "The office that approves research at the institution. Its phone, email and website follow, so governance submissions can be sent to the right place."
            ),
            ["researchGovernanceOfficePhone", "RGO Phone"],
            ["researchGovernanceOfficeEmail", "RGO Email"],
            ["researchGovernanceOfficeWebsite", "RGO Website"]
        ),
        field(
            "researchGovernanceOfficeIsNMAScheme",
            "NMA Scheme",
            "Whether the institution accepts ethics approvals under the National Mutual Acceptance scheme, so a single ethics review covers it."
        ),
        field(
            "legalName",
            "Institution Legal Name",
            "The legal entity named on agreements, which can differ from the institution's usual name, such as a health district."
        ),
        field(
            "agreementAddressOptionId",
            "Address Option",
            "Which address agreements use: the street address, the postal address, or a separate contracting party address, which appears when chosen."
        ),
        address(
            "contractingPartyAddress",
            "Contracting party address",
            "The address of the legal entity, used on agreements when the address option is the contracting party.",
            [
                "contractingPartyAddress2",
                "contractingPartySuburb",
                "contractingPartyPostcode",
                "contractingPartyStateId",
                "contractingPartyStateOther",
                "contractingPartyCountryId"
            ]
        ),
        field(
            "abn",
            "ABN",
            "The Australian Business Number of the legal entity, used on agreements and invoices."
        ),
        field("acn", "ACN", "The Australian Company Number, if the legal entity has one."),
        field(
            "acceptElectronicSignature",
            "Electronic signatures",
            "Whether the institution accepts electronically signed agreements, or needs wet-ink signatures."
        ),
        field(
            "active",
            "Active",
            "Clear to hide the institution from lists and selections. Its history and trial participation are kept."
        ),
        covering(
            field(
                "sourceDocuments",
                "Source Documents",
                "Documents kept for the institution, such as agreements. Add a row per document: name it, choose its file type and upload the file."
            ),
            ["name", "Name"],
            ["fileType", "File Type"],
            ["fileInformation", "File"]
        ),
        saveStop(
            "Submit saves the institution. Uploaded documents are stored when the institution is saved.",
            Permission.InstitutionUpdate
        )
    ],
    rules: [
        "Name, Code, Institution Type and Site location/area are required.",
        "The street address needs Address Line 1, Suburb/City, State/Province/Region (or State Other), ZIP/Postal Code and Country."
    ]
};

const institutionDetails: PageGuide = {
    ...institution,
    stops: [
        ...institution.stops,
        menu(
            item(
                "Details",
                "This page: the institution's identity, addresses, governance and legal details."
            ),
            item(
                "Departments",
                "The institution's departments, such as radiation oncology, each with its own address and personnel."
            ),
            item(
                "Personnel",
                "The people with roles at the institution, and at the institution for each trial."
            ),
            item(
                "Forms",
                "The QA forms the institution completes, such as facility questionnaires, with their versions and data."
            ),
            item(
                "Emails",
                "Who receives, and which template is used for, the emails sent about the institution's forms."
            ),
            item(
                "Notes",
                "Notes recorded about the institution, such as correspondence and decisions."
            )
        )
    ]
};

const department: PageGuide = {
    intro: "One department of the institution, such as radiation oncology, with its own addresses and contacts. People's roles can be placed in a department.",
    stops: [
        field(
            "name",
            "Name",
            "The department's name, as shown with personnel roles and in the institution's department list."
        ),
        address(
            "address1",
            "Street address",
            "The department's street address, if it differs from the institution's. Choose the country first so the right states are offered.",
            ["address2", "suburb", "postcode", "stateId", "stateOther", "countryId"]
        ),
        address(
            "mailAddress1",
            "Postal address",
            "Where post for the department should be sent, if different from its street address.",
            [
                "mailAddress2",
                "mailSuburb",
                "mailPostcode",
                "mailStateId",
                "mailStateOther",
                "mailCountryId"
            ]
        ),
        covering(
            field(
                "phone",
                "Phone",
                "The department's phone number, for contacting the department rather than a person."
            ),
            ["fax", "Fax"]
        ),
        field(
            "active",
            "Active",
            "Clear to hide the department from lists and role selections. Roles already in it are kept."
        ),
        saveStop(
            "Submit saves the department; it can then be chosen for personnel roles at the institution.",
            Permission.InstitutionDepartmentUpdate
        )
    ],
    rules: ["Name is required."]
};

const departmentDetails: PageGuide = {
    ...department,
    stops: [
        ...department.stops,
        menu(
            item("Details", "This page: the department's name, addresses and contacts."),
            item(
                "Personnel",
                "The people with roles in the department, such as its head and staff."
            )
        )
    ]
};

const institutionForm: PageGuide = {
    intro: "One QA form the institution completes, such as a facility questionnaire: its template, who is responsible, its status and its versions. Enter Data opens the answers.",
    stops: [
        field(
            "formTemplateId",
            "Form Template",
            "The QA form template the form is built from, which sets its questions. It is chosen when the form is created and cannot be changed afterwards."
        ),
        field(
            "currentVersion.formStatusId",
            "Status",
            "Where the current version is, such as Incomplete or Complete. The statuses available depend on the form's type, and Pending Review is offered only when its template requires review."
        ),
        field(
            "assignmentTypeId",
            "Assign To",
            "Whether a person or a role is responsible for completing the form. The matching field appears below."
        ),
        field(
            "assignedToId",
            "Assigned Personnel",
            "The person responsible for completing the form, when it is assigned to a person."
        ),
        field(
            "assignedRoleId",
            "Assigned Role",
            "The role responsible for completing the form, when it is assigned to a role; anyone holding the role at the institution can complete it."
        ),
        field(
            "currentVersion.version",
            "Version",
            "The name of the form's version. Create Version starts a new version with its own answers, keeping earlier versions readable."
        ),
        field(
            "currentVersion.formNextAutoVersionId",
            "Form Next Auto Version",
            "Whether new versions are created by hand (Manual) or automatically a year after the version was created or completed, as for an annual questionnaire."
        ),
        field(
            "currentVersion.dateNextVersion",
            "Date Next Version",
            "When the next version will be created automatically, for forms that create versions annually."
        ),
        field(
            "currentVersion.comments",
            "Version Comments",
            "Why this version exists or anything a reviewer should know about it, such as what changed from the last version. Earlier versions keep their own comments, which can be edited in the version history."
        ),
        saveStop(
            "Submit saves the form's details. The answers are saved on the Enter Data page.",
            Permission.InstitutionFormUpdate
        )
    ],
    rules: ["Form Template is required."]
};

const trialSiteRoles: RoleOwner = {
    kind: "site",
    owner: "this site for the trial",
    examples: "principal investigator, site coordinator and data manager",
    createPermission: Permission.TrialPersonnelRoleCreate,
    updatePermission: Permission.TrialPersonnelRoleUpdate
};
const supportingSiteRoles: RoleOwner = {
    kind: "supportingSite",
    owner: "this supporting site",
    examples: "the supporting site's investigator and radiation therapist",
    createPermission: Permission.TrialPersonnelRoleCreate,
    updatePermission: Permission.TrialPersonnelRoleUpdate
};
const institutionRoles: RoleOwner = {
    kind: "institution",
    owner: "this institution",
    examples: "head of department and site contacts",
    createPermission: Permission.InstitutionPersonnelCreate,
    updatePermission: Permission.InstitutionPersonnelUpdate
};
const departmentRoles: RoleOwner = {
    kind: "department",
    owner: "this department",
    examples: "the department head and staff",
    createPermission: Permission.InstitutionPersonnelCreate,
    updatePermission: Permission.InstitutionPersonnelUpdate
};

const siteBase = "/trials/:trialId/institutions/:trialInstitutionId";
const supportingSiteBase = `${siteBase}/supporting-sites/:supportingSiteId`;

const siteNotes = notesGuides(
    "this site's participation in the trial",
    Permission.TrialNoteCreate,
    Permission.TrialNoteUpdate
);
const institutionNotes = notesGuides(
    "this institution",
    Permission.InstitutionNoteCreate,
    Permission.InstitutionNoteUpdate
);

const institutionGuides: Record<string, PageGuide> = {
    "/trials/:trialId/institutions": {
        intro: "The institutions (sites) taking part in this trial, with their status, staffing, accrual and capability coverage at a glance.",
        stops: [
            filterStop(
                "Type an institution, code or status to filter. Inactive sites are hidden unless you switch them on."
            ),
            actionsStop(
                "Add adds an institution to the trial as a new site.",
                Permission.TrialInstitutionCreate
            ),
            grid(
                "Each row is one site; select it to open its details, personnel, participants and items.",
                [
                    column("Institution", "The institution taking part, by its usual name."),
                    column("Code", "The institution's short code, used in lists and reports."),
                    column(
                        "Status",
                        "Where the site is, from start-up to closed, as a coloured badge. Inactive sites show in red."
                    ),
                    column(
                        "Roles",
                        "How many people hold roles at the site for this trial; hover to see active and inactive counts."
                    ),
                    column(
                        "Accrual",
                        "Participants recruited against the site's expected accrual, as a progress bar; hover for the numbers."
                    ),
                    column(
                        "Date Activated",
                        "When the site opened to recruitment; empty until it is activated."
                    ),
                    column(
                        "Capabilities",
                        "How many of the trial's required capabilities the site covers; hover to see which are covered, by which institution, and which are missing."
                    )
                ]
            )
        ]
    },
    [`${siteBase}/details`]: trialInstitutionDetails,
    "/trials/:trialId/institutions/create": createGuide(
        trialInstitution,
        "Add an institution to the trial as a new site. Choose the institution and its group, then its starting status.",
        [
            "Once saved, the site's personnel, supporting sites, items and participants are available from its side menu."
        ],
        { omit: ["active", "closureDate", "totalAccrual"] }
    ),
    [`${siteBase}/supporting-sites`]: {
        intro: "Supporting sites: other institutions that deliver part of the trial for this site, such as a satellite treatment centre.",
        stops: [
            filterStop(
                "Type to filter by institution or code. Archived supporting sites are hidden unless you switch on inactive records."
            ),
            actionsStop(
                "Add links another institution as a supporting site.",
                Permission.TrialInstitutionUpdate
            ),
            grid(
                "Each row is one supporting site; select it to change its capabilities or manage its personnel.",
                [
                    column("Institution", "The institution providing support to this site."),
                    column("Code", "The institution's short code, used in lists and reports."),
                    column(
                        "Capabilities",
                        "The capabilities it provides for this site, which count towards this site's coverage."
                    ),
                    column(
                        "Tags",
                        "The trial's institution tags that describe the supporting site."
                    ),
                    column("Comments", "What the supporting site does for this site."),
                    column(
                        "Rank",
                        "The order supporting sites are listed in; lower numbers first."
                    ),
                    column(
                        "Archived Record",
                        "Ticked when the supporting site has been archived and no longer supports this site."
                    )
                ]
            )
        ]
    },
    [`${supportingSiteBase}/details`]: {
        ...supportingSite,
        stops: [
            ...supportingSite.stops,
            menu(
                item(
                    "Details",
                    "This page: the supporting site's institution, capabilities and tags."
                ),
                item("Personnel", "The people with roles at the supporting site for this trial.")
            )
        ]
    },
    [`${siteBase}/supporting-sites/create`]: createGuide(
        supportingSite,
        "Link a supporting site: choose the institution and the capabilities it provides for this site.",
        ["Once saved, add the supporting site's personnel from its Personnel section."],
        { omit: ["active"] }
    ),
    [`${supportingSiteBase}/personnel`]: roleListGuide(supportingSiteRoles),
    [`${supportingSiteBase}/personnel/create`]: roleCreateGuide(supportingSiteRoles),
    [`${supportingSiteBase}/personnel/:personnelRoleId`]: roleEditGuide(supportingSiteRoles),
    [`${siteBase}/capabilities`]: {
        intro: "How this site meets the trial's capabilities: each capability the trial requires or permits, and whether the site or one of its supporting sites covers it.",
        stops: [
            grid(
                "Each row is one of the trial's capabilities. Change what the site has on its Details page, or a supporting site's on its own page.",
                [
                    column("Capability", "The capability, such as a treatment technique."),
                    column(
                        "Requirement",
                        "Whether the trial requires the capability of every site, or only permits it."
                    ),
                    column(
                        "Status",
                        "Whether the capability is covered by the site or a supporting site. A required capability that is not covered needs attention."
                    ),
                    column(
                        "Covered By",
                        "The site or supporting site that provides the capability."
                    )
                ]
            )
        ]
    },
    [`${siteBase}/emails`]: emailRecipientsGuide(
        "site",
        "Who receives the emails sent about this site. The site follows the trial's recipients until it sets its own; participants and case reports can override the site in turn.",
        Permission.TrialInstitutionEmailRecipientsUpdate
    ),
    [`${siteBase}/participants`]: {
        intro: "The participants registered at this site for the trial.",
        stops: [
            filterStop(
                "Type a registration number, initials or treatment to filter. Withdrawn participants are hidden unless you switch on inactive records."
            ),
            actionsStop(
                "Add registers a new participant at this site.",
                Permission.TrialPatientCreate
            ),
            grid(
                "Each row is one participant; select it to open their registration details, items and emails.",
                participantColumns(false)
            )
        ]
    },
    [`${siteBase}/participants/:patientId/details`]: participantGuide(
        Permission.TrialPatientUpdate,
        true
    ),
    [`${siteBase}/participants/create`]: createGuide(
        participantGuide(Permission.TrialPatientCreate, false),
        "Register a participant at this site. Enter their initials and registration number, then their case type and treatment when known.",
        [
            "Once saved, the participant's items, emails and administration case report are available from the side menu."
        ],
        { omit: ["active"] }
    ),
    [`${siteBase}/items`]: {
        intro: "The items this site completes for the trial, such as agreements and governance approvals. Items are created from the trial's institution item templates that apply to the site.",
        stops: [
            filterStop("Type part of an item's name or description to filter the list."),
            actionsStop(
                "Add creates an item from one of the trial's institution item templates.",
                Permission.TrialInstitutionItemCreate
            ),
            grid("Select a row to open an item and its versions.", [
                column("Name", "The item's name, taken from the template it was created from."),
                column("Description", "What the item collects, from its template."),
                column(
                    "Status",
                    "Whether the current version awaits entry, review or is Complete."
                ),
                column(
                    "Versions",
                    "How many versions the item has; each version has its own collected data."
                )
            ])
        ]
    },
    [`${siteBase}/items/:trialInstitutionItemId`]: siteItem,
    [`${siteBase}/items/create`]: createGuide(
        siteItem,
        "Add an item for this site from one of the trial's institution item templates, with its first version.",
        ["Once saved, enter the item's data from Enter Data."],
        { omit: ["active"] }
    ),
    [`${siteBase}/items/:trialInstitutionItemId/entry`]: entryGuide(
        "this site item",
        Permission.TrialInstitutionItemInstanceUpdate
    ),
    [`${siteBase}/personnel`]: roleListGuide(trialSiteRoles),
    [`${siteBase}/personnel/create`]: roleCreateGuide(trialSiteRoles),
    [`${siteBase}/personnel/:personnelRoleId`]: roleEditGuide(trialSiteRoles),
    [`${siteBase}/notes`]: siteNotes.list,
    [`${siteBase}/notes/:noteId`]: siteNotes.item,
    [`${siteBase}/notes/create`]: siteNotes.create,

    "/institutions": {
        intro: "Every institution: hospitals, treatment centres and other organisations involved in TROG trials. An institution takes part in a trial as a site.",
        stops: [
            filterStop(
                "Type a name, code or place to filter. Inactive institutions are hidden unless you switch them on."
            ),
            actionsStop(
                "Add creates an institution. Search the list first so it is not added twice.",
                Permission.InstitutionCreate
            ),
            grid(
                "Each row is one institution; select it to open its details, departments, personnel and forms.",
                [
                    column("Name", "The institution's usual name, as shown throughout the system."),
                    column("Code", "The institution's short code, used in lists and reports."),
                    column("Type", "The institution type, such as public or private hospital."),
                    column("Country", "The country of the institution's street address."),
                    column("State", "The state or region of the institution's street address.")
                ]
            )
        ]
    },
    "/institutions/:institutionId/details": institutionDetails,
    "/institutions/create": createGuide(
        institution,
        "Add an institution. Enter its name, code, type and location, then its street address. The code cannot be changed once the institution is saved.",
        [
            "Once saved, its departments, personnel, QA forms and notes are available from the side menu, and it can be added to trials as a site."
        ],
        { omit: ["active", "formerNames"] }
    ),
    "/institutions/:institutionId/forms": {
        intro: "The QA forms the institution completes, such as facility questionnaires, each with its versions.",
        stops: [
            filterStop("Type a template, assignee or status to filter the list."),
            actionsStop(
                "Add creates a QA form for the institution from a form template.",
                Permission.InstitutionFormCreate
            ),
            grid("Select a row to open a form and its versions.", [
                column("Assigned To", "The person or role responsible for completing the form."),
                column("Status", "Where the current version is, such as Incomplete or Complete."),
                column(
                    "Versions",
                    "How many versions the form has; annual forms gain a version each year."
                ),
                column("Form Template", "The QA form template the form was built from.")
            ])
        ]
    },
    "/institutions/:institutionId/forms/:institutionFormId": institutionForm,
    "/institutions/:institutionId/forms/create": createGuide(
        institutionForm,
        "Create a QA form for the institution. Choose the template, who is responsible for it, and how new versions are created.",
        ["Once saved, the assignee completes the form from Enter Data."],
        {}
    ),
    "/institutions/:institutionId/forms/:institutionFormId/entry": entryGuide(
        "this institution form",
        Permission.InstitutionFormDataEntryUpdate
    ),
    "/institutions/:institutionId/emails": emailRecipientsGuide(
        "institution",
        "Who receives, and which template is used for, the emails sent about this institution's forms. The institution follows the global defaults until it sets its own; each form can override the institution in turn.",
        Permission.InstitutionEmailRecipientsUpdate
    ),
    "/institutions/:institutionId/notes": institutionNotes.list,
    "/institutions/:institutionId/notes/:noteId": institutionNotes.item,
    "/institutions/:institutionId/notes/create": institutionNotes.create,
    "/institutions/:institutionId/personnel": roleListGuide(institutionRoles),
    "/institutions/:institutionId/personnel/create": roleCreateGuide(institutionRoles),
    "/institutions/:institutionId/personnel/:personnelRoleId": roleEditGuide(institutionRoles),
    "/institutions/:institutionId/departments": {
        intro: "The institution's departments, such as radiation oncology. A person's role at the institution can be placed in a department.",
        stops: [
            filterStop(
                "Type part of a department's name to filter. Inactive departments are hidden unless you switch them on."
            ),
            actionsStop(
                "Add creates a department. Departments can also be added from a personnel role form.",
                Permission.InstitutionDepartmentCreate
            ),
            grid("Select a row to open a department and its personnel.", [
                column("Name", "The department's name, as shown with personnel roles.")
            ])
        ]
    },
    "/institutions/:institutionId/departments/:departmentId/details": departmentDetails,
    "/institutions/:institutionId/departments/create": createGuide(
        department,
        "Add a department to the institution. Only the name is required; add addresses when they differ from the institution's.",
        ["Once saved, add the department's personnel from its Personnel section."],
        { omit: ["active"] }
    ),
    "/institutions/:institutionId/departments/:departmentId/personnel":
        roleListGuide(departmentRoles),
    "/institutions/:institutionId/departments/:departmentId/personnel/create":
        roleCreateGuide(departmentRoles),
    "/institutions/:institutionId/departments/:departmentId/personnel/:personnelRoleId":
        roleEditGuide(departmentRoles)
};

export default institutionGuides;
